/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getUrlSync, isOboRequest, msdyn365Commerce } from '@msdyn365-commerce/core';
import {
    getUpdatedRefinementCriteria,
    IRefinerApplyProps,
    IRefinerChoiceSummaryClickNotification,
    IRefinerChoiceSummaryProps,
    IRefinerFooterProps,
    IRefinerHeaderProps,
    IRefinerItemData,
    IRefinerItemToggleNotification,
    IRefinerModalProps,
    IRefinerModalToggleProps,
    IRefinerProps,
    IRefinerSortOption,
    IRefinerSortProps,
    IRefinerToggleProps,
    isMatchingRefinementCriterion,
    RefinerComponent,
    RefinerOptionsComponent,
    RefinerOptionsType,
    RefinerSortComponent
} from '@msdyn365-commerce/components';
import {
    BusinessPartnerOperationDeliveryType,
    BusinessPartnerUser,
    BusinessPartnerUsersDataActions,
    InvoicePaidStatus,
    InvoiceSearchCriteria,
    IQueryResultSettings,
    OrderInvoice
} from '@msdyn365-commerce/retail-proxy';
import { getCurrentAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import {
    ArrayExtensions,
    getAccessibleBusinessPartnerUsers,
    GetAccessibleBusinessPartnerUsersInput,
    getCustomer,
    GetCustomerInput,
    getInvoices,
    GetInvoicesInput,
    getOrganizationUsers,
    IQueryResultSettingsModuleConfig,
    B2bPermission,
    isB2bActionPermitted,
    OrganizationUsersInput,
    QueryResultSettingsProxy,
    StringExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Alert,
    Button,
    Heading,
    IModuleProps,
    isMobile,
    toRecipientEmailsByOrganizationAccounts,
    VariantType
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { observable, reaction, transaction } from 'mobx';
import { observer } from 'mobx-react';

import { InvoiceErrorComponent } from './components/invoice-error-component';
import { InvoicesFilterComponent, InvoicesFilterState } from './components/invoices-filter-component';
import {
    InvoicesOrganizationWideSelectorComponent,
    InvoicesOrganizationWideSelectorState
} from './components/invoices-organization-wide-selector.component';
import { InvoicesTableComponent } from './components/invoices-table/invoices-table-component';
import { IMakePaymentResources, MakePaymentComponent } from './components/make-payment-component';
import { RequestInvoiceButton } from './components/request-invoice-button';
import { RequestInvoiceModal } from './components/request-invoice-modal';
import {
    getDateFromString,
    mapContactsIntoDeduplicatedRefinerItems,
    mapChannelIdentityIntoIRefinerItemData,
    mapDateRefinerOptionIntoIRefinerItemData,
    mapInvoiceStatusRefinerOptionIntoIRefinerItemData,
    mapOrganizationIntoIRefinerItemData
} from './common/refiner-option-mappings';
import { DateRefinerOptionItem, IDateRange, InvoiceRefinerSourceType, InvoiceStatusRefinerOptionItem } from './common/refiner-option';
import { InvoiceSortOption } from './common/sort-option';
import { mapIRefinerSortOptionIntoSortOption, mapSortOptionIntoIRefinerSortOption } from './common/sort-option-mappings';
import { InvoiceModel } from './helpers/invoice-data';
import { IAddInvoicesToCartFailureResult } from './helpers/pay-invoices';
import { IInvoicesListData } from './invoices-list.data';
import { IInvoicesListProps } from './invoices-list.props.autogenerated';

export type InvoicesListStatus = 'LOADING' | 'SUCCESS' | 'FAILED' | 'EMPTY';
export type InvoiceErrorHost = 'ADDINVOICETOCART';
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface IInvoicesListViewProps extends IInvoicesListProps<IInvoicesListData> {
    moduleProps: IModuleProps;

    header: JSX.Element;
    filter: JSX.Element;
    content?: JSX.Element;
    invoiceRequestModal?: JSX.Element;
    pagination?: JSX.Element;
}

export interface IInvoiceErrorState {
    errorHost?: InvoiceErrorHost;
    errorReason?: IAddInvoicesToCartFailureResult;
    errorMessage: string;
    otherError?: string;
    errorCode?: string;
}

export interface IInvoicesListState {
    invoices: InvoiceModel[];
    invoicesFilterState: InvoicesFilterState;
    errorState: IInvoiceErrorState;
    isInvoiceRequestModalOpen: boolean;
    isLoading: boolean;
    isOpen: boolean;
    isPageLoaded: boolean;
    payInvoiceErrorModalOpen: boolean;
    selectedButtonElement?: HTMLButtonElement | null;
    organizationWideSelectorState?: InvoicesOrganizationWideSelectorState;
    invoiceNumberRefinerValue?: string;
    refinerRenderVersion?: number;
    hasMoreInvoices?: boolean;
    isContactModelInvoice?: boolean;
    organizationUsers?: BusinessPartnerUser[];
    accessibleUsers?: BusinessPartnerUser[];
    recipientEmails: string[];
    selectedRecipientEmail: string;
}

interface IInvoicesListViewRefinerState {
    isModalOpen: boolean;
    isExpanded: boolean;
    currentSortOption?: IRefinerSortOption;
}

/**
 * InvoicesList component.
 * @extends {React.Component<IInvoicesListProps<IInvoicesListData>, IInvoicesListState>}
 */
@observer
class InvoicesList extends React.Component<IInvoicesListProps<IInvoicesListData>, IInvoicesListState> {
    @observable private _refinerState!: IInvoicesListViewRefinerState;

    @observable private _activeRefinementCriteria: IRefinerItemData[] = [];

    @observable private _viewport: GridSize;

    private readonly _className: string = 'msc-invoices-list';

    private readonly payInvoiceRequestReference: React.RefObject<HTMLButtonElement>;

    private readonly makePaymentReference: React.RefObject<HTMLButtonElement>;

    private readonly refinerModalToggleReference: React.RefObject<HTMLButtonElement>;

    private buttonChildReference: React.RefObject<HTMLButtonElement>[] = [];

    private buttonChildPayButtonReference: React.RefObject<HTMLButtonElement>[] = [];

    private readonly _queryResultSettings: IQueryResultSettings;

    private invoiceIds: string[] = [];

    private _invoicesRequestId: number = 0;

    constructor(props: IInvoicesListProps<IInvoicesListData>) {
        super(props);

        this._initializeRefinerState();
        this._viewport =
            props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
        this._queryResultSettings = QueryResultSettingsProxy.fromModuleData(
            this.props.context.request,
            this.props.config as IQueryResultSettingsModuleConfig
        ).QueryResultSettings;
        this.payInvoiceRequestReference = React.createRef<HTMLButtonElement>();
        this.makePaymentReference = React.createRef<HTMLButtonElement>();
        this.refinerModalToggleReference = React.createRef<HTMLButtonElement>();
        this._updateInvoicesState = this._updateInvoicesState.bind(this);
        this._onAddToCartFailed = this._onAddToCartFailed.bind(this);
        this._onToggleInvoiceRequestModal = this._onToggleInvoiceRequestModal.bind(this);
        this._handleFilterStateChange = this._handleFilterStateChange.bind(this);
        this._loadMoreInvoices = this._loadMoreInvoices.bind(this);
        this._requestInvoice = this._requestInvoice.bind(this);
        this._requestSpecificInvoice = this._requestSpecificInvoice.bind(this);
        this._setPayInvoiceErrorModalClose = this._setPayInvoiceErrorModalClose.bind(this);
        this._updateViewport = this._updateViewport.bind(this);
        this.state = {
            invoices: [],
            invoicesFilterState: InvoicesFilterState.ShowAll,
            isLoading: true,
            isOpen: true,
            isPageLoaded: false,
            errorState: { errorMessage: '' },
            isInvoiceRequestModalOpen: false,
            payInvoiceErrorModalOpen: false,
            selectedButtonElement: null,
            organizationWideSelectorState: InvoicesOrganizationWideSelectorState.CurrentUser,
            invoiceNumberRefinerValue: '',
            refinerRenderVersion: 0,
            recipientEmails: [],
            selectedRecipientEmail: ''
        };
    }

    public shouldComponentUpdate(nextProps: IInvoicesListProps<IInvoicesListData>, nextState: IInvoicesListState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const {
            config: { className = '', heading },
            context: {
                request: { channel, user },
                cultureFormatter: {
                    formatCurrency,

                    // @ts-expect-error
                    currencyCode
                }
            },
            data: { customerBalance },
            resources: { invoicesListTitle, invoicesListDescTitle, invoicesListDescCount }
        } = this.props;

        // Viewing invoices requires the AllowViewInvoices permission. Before this permission model the
        // module rendered for all B2B users (isLegacyAdmin: true), so the granular check only restricts
        // access when the B2BContact permission model is on.
        const canViewInvoices = isB2bActionPermitted({
            isB2bContact: this.props.context.request.user.isB2bContact,
            isLegacyAdmin: true,
            permissions: this.props.data.b2bUserInformation?.result?.Permissions,
            required: B2bPermission.AllowViewInvoices
        });
        if (!canViewInvoices) {
            return null;
        }

        const totalAmount = customerBalance.result?.OpenInvoiceBalance || 0;
        const amountText = `${formatCurrency(totalAmount.toFixed(2), currencyCode)} ${channel?.Currency || 'USD'}`;

        const totalCount = customerBalance.result?.OpenInvoiceCount || 0;
        const countText = invoicesListDescCount.replace('{0}', totalCount.toString());
        const invoicesListTitleHeading = heading?.text ?? invoicesListTitle;

        const headerClassName = `${this._className}__header`;

        const header = (
            <div className={headerClassName}>
                <Heading className={`${headerClassName}-heading`} text={invoicesListTitleHeading} />
                {!user?.isB2bContact && (
                    <div className={`${headerClassName}-description`}>
                        <div className={`${headerClassName}-description-title`}>{invoicesListDescTitle}</div>
                        <div className={`${headerClassName}-description-amount`}>{amountText}</div>
                        <div className={`${headerClassName}-description-count`}>{countText}</div>
                    </div>
                )}
            </div>
        );

        const viewProps: IInvoicesListViewProps = {
            ...(this.props as IInvoicesListProps<IInvoicesListData>),
            moduleProps: {
                moduleProps: this.props,
                className: classnames(this._className, className)
            },
            header,
            invoiceRequestModal: this._renderInvoiceRequestModal(),
            filter: this._renderInvoicesFilter(this._className),
            pagination: this._renderPagination()
        };

        if (!this.state.isPageLoaded) {
            return this.props.renderView(viewProps);
        }

        if (ArrayExtensions.hasElements(this.state.invoices)) {
            viewProps.content = this._renderListContainer();
        } else {
            viewProps.content = this._renderEmptyInvoiceLinesPageContent();
        }

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public async componentDidMount(): Promise<void> {
        if (msdyn365Commerce.isBrowser && window.addEventListener) {
            window.addEventListener('resize', this._updateViewport);
            this._updateViewport();
        }

        // isB2bContact is set during render only on a B2B channel with the multi-outlet feature on and the
        // user signed in, so it already implies the B2B-channel + feature gate — no extra checks needed.
        const isContactModelInvoice = this.props.context.request.user?.isB2bContact === true;

        this.setState({ isContactModelInvoice });

        await this.props.data.customer;
        void this._loadRecipientEmails();
        const invoicesData = await this.props.data.invoices;
        this._initializeWithInvoices(invoicesData);

        reaction(
            () => this.state.isInvoiceRequestModalOpen,
            async () => {
                if (this.invoiceIds && !this.state.isInvoiceRequestModalOpen) {
                    const context = { callerContext: this.props.context.actionContext };
                    const b2bUserInfo = this.props.data.b2bUserInformation?.result;
                    const isB2bContact = this.state.isContactModelInvoice === true;
                    const b2bUserId: string = (isB2bContact ? b2bUserInfo?.ContactPersonId : b2bUserInfo?.B2BUserId) || '';
                    for (const invoiceId of this.invoiceIds) {
                        await BusinessPartnerUsersDataActions.requestInvoiceCopyAsync(
                            context,
                            b2bUserId,
                            invoiceId,
                            BusinessPartnerOperationDeliveryType.Email,
                            isB2bContact ? this.state.selectedRecipientEmail || undefined : undefined
                        );
                    }
                    this.invoiceIds = [];
                }
            }
        );
    }

    public componentWillUnmount(): void {
        if (msdyn365Commerce.isBrowser && window.removeEventListener) {
            window.removeEventListener('resize', this._updateViewport);
        }
    }

    private _initializeWithInvoices(invoicesData: OrderInvoice[]): void {
        const invoices = invoicesData.map((invoice, index) => {
            this.buttonChildReference[Number(index)] = React.createRef<HTMLButtonElement>();
            this.buttonChildPayButtonReference[Number(index)] = React.createRef<HTMLButtonElement>();
            return new InvoiceModel(invoice, this.props.context.request.user?.isB2bContact);
        });
        this.setState({
            invoices,
            hasMoreInvoices: this._hasMoreInvoices(invoicesData),
            isLoading: false,
            isPageLoaded: true
        });
    }

    private _addInvoices(invoicesData: OrderInvoice[]): void {
        const newInvoices = invoicesData.map(invoice => new InvoiceModel(invoice, this.props.context.request.user?.isB2bContact));
        this.setState({
            invoices: this.state.invoices.concat(newInvoices),
            hasMoreInvoices: this._hasMoreInvoices(invoicesData),
            isLoading: false
        });
    }

    private _renderEmptyInvoiceLinesPageContent(): JSX.Element {
        const containerClass = `${this._className}__empty-lines__container`;
        const actionsContainerClass = `${containerClass}__actions`;
        return (
            <div className={containerClass}>
                <div className={`${containerClass}__title`}>{this.props.resources.invoiceEmptyInvoiceLinesTitle}</div>
                <div className={`${containerClass}__description`}>{this.props.resources.invoiceEmptyInvoiceLinesDescription}</div>
                <div className={actionsContainerClass}>
                    <Button
                        className={`${actionsContainerClass}__continue-shopping`}
                        aria-label={this.props.resources.invoiceEmptyInvoiceLinesContinueShoppingButtonAriaLabel}
                        href={getUrlSync('home', this.props.context.actionContext)}
                    >
                        {this.props.resources.invoiceEmptyInvoiceLinesContinueShoppingButtonText}
                    </Button>
                </div>
            </div>
        );
    }

    private _renderListContainer(): JSX.Element {
        const {
            context: {
                request: { user }
            }
        } = this.props;
        const containerClass = `${this._className}__container`;
        const containerHeaderClass = `${containerClass}__header`;
        const containerContentClass = `${containerClass}__content`;
        const isOnBehalfOfRequest = isOboRequest(this.props.context.request);

        return (
            <div className={containerClass}>
                <div className={containerHeaderClass}>
                    {this._renderMakePayment(containerHeaderClass, isOnBehalfOfRequest)}
                    {this._renderInvoiceRequestButton()}
                </div>
                {user?.isB2bContact && this._renderOrganizationWideSelector()}
                {user?.isB2bContact && <RefinerComponent {...this._getRefinerProps()} />}
                <div className={containerContentClass}>{this._renderInvoiceErrors()}</div>
                <div className={containerContentClass}>{this._renderInvoicesList(containerContentClass)}</div>
            </div>
        );
    }

    private _updateInvoicesState(): void {
        this.setState({
            invoices: this.state.invoices
        });
    }

    private async _getInvoicesSearchCriteria(state: InvoicesFilterState): Promise<InvoiceSearchCriteria> {
        const {
            context: {
                actionContext,
                request: { user }
            }
        } = this.props;
        const searchCriteria: InvoiceSearchCriteria = {};

        if (user?.isB2bContact && user?.customerAccountNumber) {
            this._applyOrganizationWideSelectorCriteria(searchCriteria, user?.customerAccountNumber);
        } else {
            const customer = await getCustomer(new GetCustomerInput(actionContext?.requestContext.apiSettings), actionContext);
            const businessPartner = await getCurrentAsync({ callerContext: actionContext });
            searchCriteria.BusinessPartnerId = businessPartner?.BusinessPartnerId;
            searchCriteria.CustomerId = customer?.AccountNumber;
        }

        switch (state) {
            case InvoicesFilterState.ShowAll:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.None];
                break;
            case InvoicesFilterState.Paid:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.Paid];
                break;
            case InvoicesFilterState.PartiallyPaid:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.PartiallyPaid];
                break;
            case InvoicesFilterState.Unpaid:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.Unpaid];
                break;
            default:
                throw new Error('Failed to process unknown invoices filter state.');
        }

        if (user?.isB2bContact && user?.customerAccountNumber) {
            this._applyRefinementCriteria(searchCriteria);
        }

        return searchCriteria;
    }

    private async _handleFilterStateChange(state: InvoicesFilterState): Promise<void> {
        this.setState({
            invoicesFilterState: state,
            isLoading: true
        });

        const requestId = ++this._invoicesRequestId;
        const searchCriteria = await this._getInvoicesSearchCriteria(state);

        if (this._queryResultSettings.Paging) {
            this._queryResultSettings.Paging.Skip = 0;
        }

        const invoices = await getInvoices(
            new GetInvoicesInput(searchCriteria, this._queryResultSettings, false, this._isDescendingSort()),
            this.props.context.actionContext
        );
        if (requestId !== this._invoicesRequestId) {
            return;
        }
        this._initializeWithInvoices(invoices);
    }

    private _renderInvoicesFilter(className: string): JSX.Element {
        const containerClass = `${this._className}__container`;
        const containerContentClass = `${containerClass}__content`;
        const filterClassName = `${className}__filter`;

        if (this.props.context.request.user?.isB2bContact) {
            return <div className={containerContentClass}>{this._renderInvoiceInCartPaymentMessage()}</div>;
        }

        return (
            <div>
                <div className={containerContentClass}>{this._renderInvoiceInCartPaymentMessage()}</div>
                <InvoicesFilterComponent
                    id={filterClassName}
                    typeName={this.props.typeName}
                    key={filterClassName}
                    className={filterClassName}
                    context={this.props.context}
                    resources={this.props.resources}
                    data={{}}
                    currentState={InvoicesFilterState.ShowAll}
                    onFilterStateChanged={this._handleFilterStateChange}
                    disabled={this.state.isLoading}
                />
            </div>
        );
    }

    private _renderInvoicesList(className: string): JSX.Element {
        const tableClassName = `${className}__table`;
        return (
            <InvoicesTableComponent
                invoiceButtonReferences={this.buttonChildReference}
                payInvoiceButtonReferences={this.buttonChildPayButtonReference}
                id={tableClassName}
                typeName={this.props.typeName}
                key={tableClassName}
                className={tableClassName}
                data={{ invoices: this.state.invoices, customer: this.props.data.customer.result! }}
                context={this.props.context}
                resources={this.props.resources}
                onChange={this._updateInvoicesState}
                onError={this._onAddToCartFailed}
                onRequestInvoice={this._requestSpecificInvoice}
            />
        );
    }

    private _onAddToCartFailed(newErrorState: IInvoiceErrorState, reference?: React.RefObject<HTMLButtonElement>): void {
        const errorState = { ...newErrorState };
        errorState.errorHost = 'ADDINVOICETOCART';
        errorState.errorMessage =
            newErrorState.errorReason?.failureReason === 'EMPTYINPUT'
                ? this.props.resources.invoiceErrorInvoiceIdNotExist
                : this.props.resources.invoiceErrorFailedAddInvoiceToCart;

        const shouldShowAdditionalInfoInPopup =
            errorState.errorCode === 'Microsoft_Dynamics_Commerce_Runtime_InvoiceLineNotAllowedWithItemLines';
        this.setState({ errorState, payInvoiceErrorModalOpen: shouldShowAdditionalInfoInPopup, selectedButtonElement: reference?.current });
    }

    private _renderMakePayment(className: string, disabled: boolean): JSX.Element {
        const id = `${className}__make-payment`;
        const makePaymentResources: IMakePaymentResources = {
            makePaymentButtonText: this.props.resources.makePaymentButtonText,
            makePaymentButtonAriaLabel: this.props.resources.makePaymentButtonAriaLabel
        };

        const selectedInvoices = this._getSelectedInvoices();

        return (
            <MakePaymentComponent
                id={id}
                typeName={this.props.typeName}
                key={id}
                data={{ invoices: selectedInvoices }}
                context={this.props.context}
                telemetry={this.props.telemetry}
                moduleId={this.props.id}
                moduleTypeName={this.props.typeName}
                navigationUrl={getUrlSync('cart', this.props.context.actionContext)}
                resources={makePaymentResources}
                onError={this._onAddToCartFailed}
                buttonReferences={this.makePaymentReference}
                disabled={disabled}
            />
        );
    }

    private _setPayInvoiceErrorModalClose(): void {
        this.setState({
            payInvoiceErrorModalOpen: false
        });
        this.state.selectedButtonElement?.focus();
    }

    private _renderInvoiceErrors(): JSX.Element {
        const {
            resources: {
                payInvoiceErrorDialogHeader,
                payInvoiceErrorDialogBodyErrorTitle,
                payInvoiceErrorDialogBodyErrorDescription,
                payInvoiceErrorDialogFooterGoToCartButtonText,
                payInvoiceErrorDialogFooterCancelButtonText
            }
        } = this.props;

        const cartUrl = getUrlSync('cart', this.props.context.actionContext) || '';

        const errorClassName = `${this._className}__errors`;

        return (
            <InvoiceErrorComponent
                id={errorClassName}
                typeName={this.props.typeName}
                data={{}}
                messageHeader={this.props.resources.invoiceErrorMessageHeader}
                context={this.props.context}
                errorMessage={this.state.errorState.errorMessage}
                otherError={this.state.errorState.otherError}
                showError={this.state.errorState.errorHost === 'ADDINVOICETOCART'}
                payInvoiceErrorModalOpen={this.state.payInvoiceErrorModalOpen}
                errorDialogHeader={payInvoiceErrorDialogHeader}
                errorDialogBodyErrorTitle={payInvoiceErrorDialogBodyErrorTitle}
                errorDialogBodyErrorDescription={payInvoiceErrorDialogBodyErrorDescription}
                errorDialogFooterGoToCartButtonText={payInvoiceErrorDialogFooterGoToCartButtonText}
                errorDialogFooterCancelButtonText={payInvoiceErrorDialogFooterCancelButtonText}
                cartUrl={cartUrl}
                setPayInvoiceErrorModalClose={this._setPayInvoiceErrorModalClose}
            />
        );
    }

    private _renderInvoiceInCartPaymentMessage(): JSX.Element {
        const cart = this.props.data.cart?.result;

        if (!cart?.hasInvoiceLine) {
            return <></>;
        }

        return (
            <Alert className='msc-alert-invoice' isOpen={this.state.isOpen}>
                <div className='msc-alert-invoice__header' aria-hidden='true'>
                    <span className='msi-exclamation-triangle' />
                    <span>{this.props.resources.invoiceInCartPaymentMessage}</span>
                    <button className='toggle' onClick={this._onDismiss}></button>
                </div>
            </Alert>
        );
    }

    // Dismiss alert.
    private readonly _onDismiss = () => {
        this.setState({
            isOpen: false
        });
    };

    private _renderPagination(): JSX.Element | undefined {
        const { data, resources } = this.props;
        const hasMoreInvoices = this.state.hasMoreInvoices ?? this._hasMoreInvoices(data?.invoices?.result);
        if (!hasMoreInvoices) {
            return undefined;
        }

        return (
            <Button
                className={classnames(`${this._className}__load-more`, this.state.isLoading ? 'is-busy' : '')}
                disabled={this.state.isLoading}
                onClick={this._loadMoreInvoices}
            >
                {resources.invoicesLoadMore}
            </Button>
        );
    }

    private _hasMoreInvoices(invoices?: OrderInvoice[]): boolean {
        const pageSize = this._queryResultSettings.Paging?.Top;
        return Boolean(pageSize && invoices && invoices.length >= pageSize);
    }

    private async _loadMoreInvoices(): Promise<void> {
        this.setState({ isLoading: true });
        const requestId = ++this._invoicesRequestId;
        const searchCriteria = await this._getInvoicesSearchCriteria(this.state.invoicesFilterState);
        if (this._queryResultSettings.Paging) {
            this._queryResultSettings.Paging.Skip! += this._queryResultSettings.Paging.Top!;
        }
        const newInvoices = await getInvoices(
            new GetInvoicesInput(searchCriteria, this._queryResultSettings, false, this._isDescendingSort()),
            this.props.context.actionContext
        );
        if (requestId !== this._invoicesRequestId) {
            return;
        }
        this._addInvoices(newInvoices);
    }

    private _renderInvoiceRequestModal(): JSX.Element {
        const customer = this.props.data.customer.result;
        const {
            invoiceRequestModalCancelLabel,
            invoiceRequestModalSubmitLabel,
            invoiceRequestModalHeaderLabel,
            invoiceRequestModalSendToEmailLabel,
            invoiceRequestModalSearch,
            invoiceRequestModalInvoiceSearchDisplay,
            invoiceRequestModalSingleInvoiceSearchDisplay,
            invoiceRequestModalSearchButton
        } = this.props.resources;

        return (
            <RequestInvoiceModal
                className={this._className}
                email={customer?.Email || ''}
                isModalOpen={this.state.isInvoiceRequestModalOpen}
                toggle={this._onToggleInvoiceRequestModal}
                cancelLabel={invoiceRequestModalCancelLabel}
                modalHeaderLabel={invoiceRequestModalHeaderLabel}
                sendToEmailLabel={invoiceRequestModalSendToEmailLabel}
                submitRequestLabel={invoiceRequestModalSubmitLabel}
                onSubmitRequest={this._requestInvoice}
                invoices={this.state.invoices}
                searchPlaceholderLabel={invoiceRequestModalSearch}
                invoicesFoundString={invoiceRequestModalInvoiceSearchDisplay}
                singleInvoiceFoundString={invoiceRequestModalSingleInvoiceSearchDisplay}
                searchButtonAltText={invoiceRequestModalSearchButton}
                resources={this.props.resources}
                recipientEmails={this.state.recipientEmails}
                selectedRecipientEmail={this.state.selectedRecipientEmail}
                onChangeRecipientEmail={this._updateRecipientEmail}
            />
        );
    }

    // Self only outside the contact model (today's behavior); the org's accessible contacts under it.
    private readonly _loadRecipientEmails = async (): Promise<void> => {
        const selfEmail = this.props.data.customer.result?.Email?.trim() ?? '';
        const isB2bContact = this.props.context.request.user?.isB2bContact === true;

        if (!isB2bContact) {
            this.setState({ recipientEmails: selfEmail ? [selfEmail] : [], selectedRecipientEmail: selfEmail });
            return;
        }

        try {
            // Await the org info so the org-scoping filter below is never skipped on a still-pending result.
            const organizationAccountNumber = (await this.props.data.b2bUserInformation)?.OrganizationCustomerAccountNumber;
            const users = await BusinessPartnerUsersDataActions.getAccessibleUsersAsync({
                callerContext: this.props.context.actionContext
            });
            const organizationAccountNumbers = organizationAccountNumber ? [organizationAccountNumber] : [];
            const emails = toRecipientEmailsByOrganizationAccounts(users, organizationAccountNumbers, selfEmail);
            // Default to self explicitly rather than emails[0], so the default never relies on array ordering.
            this.setState({ recipientEmails: emails, selectedRecipientEmail: selfEmail || emails[0] || '' });
        } catch (error) {
            this.props.context.telemetry.error(`Failed to load accessible recipient emails: ${error}`);
            this.setState({ recipientEmails: selfEmail ? [selfEmail] : [], selectedRecipientEmail: selfEmail });
        }
    };

    private readonly _updateRecipientEmail = (email: string): void => {
        this.setState({ selectedRecipientEmail: email });
    };

    private _onToggleInvoiceRequestModal(): void {
        this.setState({
            isInvoiceRequestModalOpen: !this.state.isInvoiceRequestModalOpen
        });
        if (this.state.isInvoiceRequestModalOpen) {
            this.state.selectedButtonElement?.focus();
        }
    }

    /**
     * Request specific invoice function.
     */
    private readonly _onToggleInvoiceRequestParentModal = () => {
        this.setState(
            {
                selectedButtonElement: this.payInvoiceRequestReference.current
            },
            () => {
                this._onToggleInvoiceRequestModal();
            }
        );
    };

    private _renderInvoiceRequestButton(): JSX.Element | null {
        const { invoiceRequestButtonLabel } = this.props.resources;
        const customer = this.props.data.customer.result;

        if (StringExtensions.isNullOrWhitespace(customer?.Email)) {
            return null;
        }

        return (
            <RequestInvoiceButton
                innerReference={this.payInvoiceRequestReference}
                requestInvoiceButtonLabel={invoiceRequestButtonLabel}
                onClicked={this._onToggleInvoiceRequestParentModal}
                className={this._className}
            />
        );
    }

    private _requestInvoice(invoiceId: string): void {
        this.invoiceIds.push(invoiceId);
        this._onToggleInvoiceRequestModal();
    }

    private _getSelectedInvoices(): InvoiceModel[] {
        let selectedInvoices = this.state.invoices.filter(invoice => invoice.isSelected);
        if (!ArrayExtensions.hasElements(selectedInvoices)) {
            selectedInvoices = this.state.invoices;
        }

        return selectedInvoices;
    }

    private _requestSpecificInvoice(
        specificInvoice: InvoiceModel,
        requestInvoiceButtonReference: React.RefObject<HTMLButtonElement>
    ): void {
        this.state.invoices.forEach((invoice: InvoiceModel) => {
            if (invoice === specificInvoice) {
                invoice.isSelected = true;
            } else {
                invoice.isSelected = false;
            }
        });
        this.setState({
            selectedButtonElement: requestInvoiceButtonReference.current
        });
        this._onToggleInvoiceRequestModal();
    }

    private _initializeRefinerState(): void {
        this._refinerState = {
            isModalOpen: false,
            isExpanded: false,
            currentSortOption: this._refinerState?.currentSortOption ?? this._getDefaultSortOption()
        };
    }

    private _getRefinerProps(): IRefinerProps {
        return {
            context: this.props.context,
            id: this.props.id,
            typeName: this.props.typeName,
            data: {},
            refinerOptions: this._getRefinerOptions(),
            isMobileView: this._isMobileView(),
            isExpanded: this._refinerState.isExpanded,
            refinerModalToggleProps: this._getRefinerModalToggleProps(),
            refinerModalProps: this._getRefinerModalProps(),
            refinerHeaderProps: this._getRefinerHeaderProps(),
            refinerFooterProps: this._getRefinerFooterProps()
        };
    }

    private _getRefinerHeaderProps(): IRefinerHeaderProps {
        const selectedRefiners = this._activeRefinementCriteria ?? [];

        return {
            context: this.props.context,
            id: this.props.id,
            typeName: this.props.typeName,
            data: {},
            refinerToggleProps: this._getRefinerToggleProps(),
            refinerChoiceSummaryProps: selectedRefiners.length > 0 ? this._getRefinerChoiceSummaryProps(selectedRefiners) : undefined,
            refinerSortProps: this._getRefinerSortProps(),
            // On mobile, filter and sort are surfaced as two separate controls (Figma): the modal toggle acts as the
            // "Filter" button, and the sort dropdown is rendered alongside it via extraActions. On desktop the sort is
            // already shown inline through refinerSortProps, so extraActions is omitted to avoid duplicating it.
            extraActions: this._isMobileView() ? <RefinerSortComponent {...this._getRefinerSortProps()} /> : undefined
        };
    }

    private _getRefinerFooterProps(): IRefinerFooterProps {
        return {
            context: this.props.context,
            id: this.props.id,
            typeName: this.props.typeName,
            data: {},
            refinerApplyProps: this._getRefinerApplyProps()
        };
    }

    private _getRefinerModalProps(): IRefinerModalProps {
        const filterText = this.props.resources.filterText || 'Filter';
        return {
            context: this.props.context,
            id: this.props.id,
            typeName: this.props.typeName,
            data: {},
            modalTitle: filterText,
            modalCloseButtonText: this.props.resources.doneText || 'Done',
            isOpen: this._refinerState.isModalOpen,
            returnRef: this.refinerModalToggleReference,
            refinerOptions: this._getRefinerOptions(),
            onToggle: () => {
                this._toggleRefinerModal();
                void this._refreshInvoices();
            }
        };
    }

    private _getRefinerToggleProps(): IRefinerToggleProps {
        const filterText = this.props.resources.filterText || 'Filter';
        return {
            context: this.props.context,
            id: this.props.id,
            typeName: this.props.typeName,
            data: {},
            text: filterText,
            ariaLabel: this.props.resources.filterTextAriaLabel || filterText,
            isExpanded: this._refinerState.isExpanded,
            onClick: () => {
                this._refinerState.isExpanded = !this._refinerState.isExpanded;
                this._refreshSelectedRefiners();
            }
        };
    }

    private _getRefinerModalToggleProps(): IRefinerModalToggleProps {
        const filterText = this.props.resources.filterText || 'Filter';
        return {
            context: this.props.context,
            id: `${this.props.id}__refiner-modal-toggle`,
            typeName: this.props.typeName,
            data: {},
            text: filterText,
            ariaLabel: this.props.resources.filterTextAriaLabel || filterText,
            innerRef: this.refinerModalToggleReference,
            onClick: this._toggleRefinerModal
        };
    }

    private _getRefinerChoiceSummaryProps(selectedRefiners: IRefinerItemData[]): IRefinerChoiceSummaryProps {
        return {
            context: this.props.context,
            id: this.props.id,
            typeName: this.props.typeName,
            data: { selectedChoices: selectedRefiners },
            clearAllText: this.props.resources.clearAllText,
            label: '',
            choiceAriaLabel: this.props.resources.refinerChoiceAriaLabel,
            choiceFormat: '{0}',
            closeAriaLabel: this.props.resources.refinerCloseAriaLabel,
            onClickChoice: this._clickRefinerChoice,
            onClickClearAll: this._clickRefinerChoice
        };
    }

    private _getRefinerSortProps(): IRefinerSortProps {
        return {
            context: this.props.context,
            id: this.props.id,
            typeName: this.props.typeName,
            data: { options: this._getSortOptions() },
            className: `${this._className}__refiner-sort`,
            currentSortOption: this._refinerState.currentSortOption,
            label: this.props.resources.sortLabel,
            ariaLabel: this.props.resources.sortAriaLabel,
            onSelectOption: this._selectRefinerSortOption
        };
    }

    private _getRefinerApplyProps(): IRefinerApplyProps {
        const doneText = this.props.resources.doneText;
        return {
            context: this.props.context,
            id: this.props.id,
            typeName: this.props.typeName,
            data: {},
            text: doneText,
            ariaLabel: this.props.resources.doneTextAriaLabel || doneText,
            onClick: () => {
                this._refinerState.isExpanded = false;
                void this._refreshInvoices();
            }
        };
    }

    private _getRefinerOptions(): React.ReactNode[] {
        const dateOptions = [
            DateRefinerOptionItem.LastSevenDays,
            DateRefinerOptionItem.LastThirtyDays,
            DateRefinerOptionItem.LastThreeMonths,
            DateRefinerOptionItem.Customized
        ];
        const invoiceDateItems = dateOptions.map(option =>
            mapDateRefinerOptionIntoIRefinerItemData(option, InvoiceRefinerSourceType.InvoiceDate, this.props.resources)
        );
        const dueDateItems = dateOptions.map(option =>
            mapDateRefinerOptionIntoIRefinerItemData(option, InvoiceRefinerSourceType.DueDate, this.props.resources)
        );
        const statusItems = [
            mapInvoiceStatusRefinerOptionIntoIRefinerItemData(InvoiceStatusRefinerOptionItem.Unpaid, this.props.resources),
            mapInvoiceStatusRefinerOptionIntoIRefinerItemData(InvoiceStatusRefinerOptionItem.PartiallyPaid, this.props.resources),
            mapInvoiceStatusRefinerOptionIntoIRefinerItemData(InvoiceStatusRefinerOptionItem.Paid, this.props.resources)
        ];
        const contactItems = mapContactsIntoDeduplicatedRefinerItems(this._getContactRefinerSource());
        const originItems = this.props.data.channels?.result?.channelIdentities.map(mapChannelIdentityIntoIRefinerItemData) ?? [];
        const organizationItems = this.props.data.managedBusinessPartners?.result?.map(mapOrganizationIntoIRefinerItemData) ?? [];
        return [
            this._isRefinerVisible(InvoiceRefinerSourceType.InvoiceNumber) ? this._renderInvoiceNumberRefinerOption() : undefined,
            this._isRefinerVisible(InvoiceRefinerSourceType.InvoiceDate)
                ? this._renderRefinerOption(
                      InvoiceRefinerSourceType.InvoiceDate,
                      this.props.resources.invoiceDateText,
                      RefinerOptionsType.SingleSelect,
                      invoiceDateItems
                  )
                : undefined,
            this._isRefinerVisible(InvoiceRefinerSourceType.DueDate)
                ? this._renderRefinerOption(
                      InvoiceRefinerSourceType.DueDate,
                      this.props.resources.invoiceDueDateText,
                      RefinerOptionsType.SingleSelect,
                      dueDateItems
                  )
                : undefined,
            this._isRefinerVisible(InvoiceRefinerSourceType.Status)
                ? this._renderRefinerOption(
                      InvoiceRefinerSourceType.Status,
                      this.props.resources.invoiceStatusText,
                      RefinerOptionsType.MultiSelect,
                      statusItems
                  )
                : undefined,
            this._isRefinerVisible(InvoiceRefinerSourceType.Origin)
                ? this._renderRefinerOption(
                      InvoiceRefinerSourceType.Origin,
                      this.props.resources.originRefinerOptionLabel,
                      RefinerOptionsType.MultiSelect,
                      originItems
                  )
                : undefined,
            this._isRefinerVisible(InvoiceRefinerSourceType.Organization)
                ? this._renderRefinerOption(
                      InvoiceRefinerSourceType.Organization,
                      this.props.resources.organizationRefinerOptionLabel,
                      RefinerOptionsType.MultiSelect,
                      organizationItems
                  )
                : undefined,
            this._isRefinerVisible(InvoiceRefinerSourceType.Contact)
                ? this._renderRefinerOption(
                      InvoiceRefinerSourceType.Contact,
                      this.props.resources.contactRefinerOptionLabel,
                      RefinerOptionsType.MultiSelect,
                      contactItems
                  )
                : undefined
        ].filter((option): option is React.ReactElement => !!option);
    }

    private _getContactRefinerSource(): BusinessPartnerUser[] {
        switch (this.state.organizationWideSelectorState ?? InvoicesOrganizationWideSelectorState.CurrentUser) {
            case InvoicesOrganizationWideSelectorState.OrganizationWide:
                return this.state.organizationUsers ?? [];
            case InvoicesOrganizationWideSelectorState.ShowAll:
                return this.state.accessibleUsers ?? [];
            default:
                return [];
        }
    }

    /**
     * Lazily loads the contact-refiner source for the selected scope. These users are only needed once the shopper
     * switches to an organization-wide / show-all view, so we fetch on demand (and cache) instead of prefetching them
     * on every server render — which would issue unnecessary API calls for shoppers who never open those views.
     * @param selectorState - The selected organization-wide scope.
     */
    private async _ensureContactRefinerSource(selectorState: InvoicesOrganizationWideSelectorState): Promise<void> {
        const { actionContext } = this.props.context;
        const apiSettings = actionContext?.requestContext.apiSettings;

        if (selectorState === InvoicesOrganizationWideSelectorState.OrganizationWide && this.state.organizationUsers === undefined) {
            const organizationUsers = await getOrganizationUsers(new OrganizationUsersInput(apiSettings), actionContext).catch(() => []);
            this.setState({ organizationUsers: organizationUsers ?? [] });
        } else if (selectorState === InvoicesOrganizationWideSelectorState.ShowAll && this.state.accessibleUsers === undefined) {
            const accessibleUsers = await getAccessibleBusinessPartnerUsers(
                new GetAccessibleBusinessPartnerUsersInput(apiSettings),
                actionContext
            ).catch(() => []);
            this.setState({ accessibleUsers: accessibleUsers ?? [] });
        }
    }

    private _isRefinerVisible(sourceType: InvoiceRefinerSourceType): boolean {
        // Refiners only render for B2B contact users who can view invoices (guarded by the isB2bContact check and the
        // canViewInvoices render gate), so no permission re-check is needed here: visibility depends solely on the
        // selected organization-wide scope.
        const selectorState = this.state.organizationWideSelectorState ?? InvoicesOrganizationWideSelectorState.CurrentUser;

        switch (sourceType) {
            case InvoiceRefinerSourceType.InvoiceNumber:
                return true;
            case InvoiceRefinerSourceType.InvoiceDate:
            case InvoiceRefinerSourceType.DueDate:
            case InvoiceRefinerSourceType.Status:
            case InvoiceRefinerSourceType.Origin:
                return true;
            case InvoiceRefinerSourceType.Organization:
                return selectorState === InvoicesOrganizationWideSelectorState.ShowAll;
            case InvoiceRefinerSourceType.Contact:
                return (
                    selectorState === InvoicesOrganizationWideSelectorState.ShowAll ||
                    selectorState === InvoicesOrganizationWideSelectorState.OrganizationWide
                );
            default:
                return false;
        }
    }

    private _renderInvoiceNumberRefinerOption(): React.ReactElement {
        const refinerName = this.props.resources.invoiceNumberText;
        const refinerInputId = `${this.props.id}__invoice-number-refiner-input`;

        return (
            <div className={`${this._className}__refiner-options-${InvoiceRefinerSourceType.InvoiceNumber} msc-refiner-options list-group`}>
                <label className={`${this._className}__invoice-number-refiner-label`} htmlFor={refinerInputId}>
                    {refinerName}
                </label>
                <div className='collapse show'>
                    <ul className='msc-refiner-options__list' aria-label={refinerName}>
                        <li className='msc-refiner-options__list-item'>
                            <input
                                id={refinerInputId}
                                className={`${this._className}__invoice-number-refiner-input`}
                                type='text'
                                placeholder={this.props.resources.invoiceNumberRefinerPlaceholder}
                                value={this.state.invoiceNumberRefinerValue ?? ''}
                                onChange={this._updateInvoiceNumberRefiner}
                            />
                        </li>
                    </ul>
                </div>
            </div>
        );
    }

    private _renderRefinerOption(
        sourceTypeValue: InvoiceRefinerSourceType,
        name: string,
        typeValue: RefinerOptionsType,
        refinerItems: IRefinerItemData[]
    ): React.ReactElement {
        return (
            <RefinerOptionsComponent
                context={this.props.context}
                id={this.props.id}
                typeName={this.props.typeName}
                key={sourceTypeValue}
                className={`${this._className}__refiner-options-${sourceTypeValue}`}
                isDisabled={false}
                isExpandedOnInitialLoad={true}
                onUpdateRefiners={this._toggleRefinerItem}
                data={{
                    refinerOptionsId: sourceTypeValue,
                    sourceTypeValue,
                    name,
                    typeValue,
                    isExpanded: true,
                    selectedRefinerItems: this._activeRefinementCriteria,
                    refinerItems
                }}
            />
        );
    }

    private _toggleRefinerItem = (notification: IRefinerItemToggleNotification): void => {
        transaction(() => {
            this._activeRefinementCriteria = getUpdatedRefinementCriteria(notification, this._activeRefinementCriteria);
        });
        this._refreshSelectedRefiners();
    };

    private _updateInvoiceNumberRefiner = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({ invoiceNumberRefinerValue: event.target.value });
    };

    private _clickRefinerChoice = (notification: IRefinerChoiceSummaryClickNotification): void => {
        let currentRefinementCriteria = this._activeRefinementCriteria ?? [];

        if (!notification.shouldClearAll) {
            const selectedChoice = notification.selectedRefinerItem;

            if (!selectedChoice) {
                this.props.telemetry.warning(`[${this._className}._clickRefinerChoice] Choice could not be determined`);
                return;
            }

            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: IRefinerItemData) => !isMatchingRefinementCriterion(selectedChoice, selectedCriterion)
            );
        } else {
            currentRefinementCriteria = [];
        }

        transaction(() => {
            this._activeRefinementCriteria = currentRefinementCriteria;
        });

        if (notification.shouldClearAll) {
            this.setState({ invoiceNumberRefinerValue: '' }, () => {
                this._refreshSelectedRefiners();
                void this._refreshInvoices();
            });
        } else {
            this._refreshSelectedRefiners();
            void this._refreshInvoices();
        }
    };

    private _refreshSelectedRefiners = (): void => {
        if (!this.state.isPageLoaded) {
            return;
        }

        this.setState(({ refinerRenderVersion }) => ({
            refinerRenderVersion: (refinerRenderVersion ?? 0) + 1
        }));
    };

    private _updateViewport(): void {
        const previousIsMobileView = this._isMobileView();
        this._viewport = this._getViewport();
        const currentIsMobileView = this._isMobileView();

        if (this._refinerState.isModalOpen && !currentIsMobileView) {
            this._toggleRefinerModal();
        }

        if (previousIsMobileView !== currentIsMobileView) {
            this._refreshSelectedRefiners();
        }
    }

    private _getViewport(): GridSize {
        if (msdyn365Commerce.isBrowser) {
            return isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        }

        return isMobile({ variant: VariantType.Viewport, context: this.props.context.request });
    }

    private _getDefaultSortOption(): IRefinerSortOption {
        return mapSortOptionIntoIRefinerSortOption(InvoiceSortOption.NewestToOldest, this.props.resources);
    }

    private _getSortOptions(): IRefinerSortOption[] {
        return [InvoiceSortOption.NewestToOldest, InvoiceSortOption.OldestToNewest].map(option =>
            mapSortOptionIntoIRefinerSortOption(option, this.props.resources)
        );
    }

    private _selectRefinerSortOption = (sortOption?: IRefinerSortOption): void => {
        this._refinerState.currentSortOption = sortOption;

        if (!this._refinerState.isExpanded && !this._refinerState.isModalOpen) {
            void this._refreshInvoices();
        }
    };

    private _isDescendingSort(): boolean {
        const sortOption = mapIRefinerSortOptionIntoSortOption(this._refinerState.currentSortOption);

        return sortOption !== InvoiceSortOption.OldestToNewest;
    }

    private _toggleRefinerModal = (): void => {
        this._refinerState.isModalOpen = !this._refinerState.isModalOpen;
        this._refreshSelectedRefiners();
    };

    private _isMobileView(): boolean {
        const requestViewport = isMobile({ variant: VariantType.Viewport, context: this.props.context.request });
        return this._viewport === 'xs' || this._viewport === 'sm' || requestViewport === 'xs' || requestViewport === 'sm';
    }

    private _applyOrganizationWideSelectorCriteria(searchCriteria: InvoiceSearchCriteria, customerAccountNumber: string): void {
        const currentContactPersonId = this.props.data.b2bUserInformation?.result?.ContactPersonId;

        switch (this.state.organizationWideSelectorState ?? InvoicesOrganizationWideSelectorState.CurrentUser) {
            case InvoicesOrganizationWideSelectorState.CurrentUser:
                if (customerAccountNumber) {
                    searchCriteria.CustomerAccountNumbers = [customerAccountNumber];
                }
                if (currentContactPersonId) {
                    searchCriteria.ContactPersonIds = [currentContactPersonId];
                }
                break;
            case InvoicesOrganizationWideSelectorState.OrganizationWide:
                if (customerAccountNumber) {
                    searchCriteria.CustomerAccountNumbers = [customerAccountNumber];
                }
                break;
            case InvoicesOrganizationWideSelectorState.ShowAll:
                break;
            default:
                throw new Error('Failed to process unknown invoices organization-wide selector state.');
        }
    }

    private _applyRefinementCriteria(searchCriteria: InvoiceSearchCriteria): void {
        const channelIds = this._activeRefinementCriteria
            .filter(refinement => refinement.sourceTypeValue === InvoiceRefinerSourceType.Origin && !isNaN(Number(refinement.value)))
            .map(refinement => Number(refinement.value));
        if (channelIds.length > 0) {
            searchCriteria.ChannelIds = channelIds;
        }

        const paidStatusValues = this._activeRefinementCriteria
            .filter(refinement => refinement.sourceTypeValue === InvoiceRefinerSourceType.Status && !isNaN(Number(refinement.value)))
            .map(refinement => Number(refinement.value));
        if (paidStatusValues.length > 0) {
            searchCriteria.InvoicePaidStatusValues = paidStatusValues;
        }

        const customerAccountNumbers = this._activeRefinementCriteria
            .filter(refinement => refinement.sourceTypeValue === InvoiceRefinerSourceType.Organization && refinement.value)
            .map(refinement => refinement.value as string);
        if (customerAccountNumbers.length > 0) {
            searchCriteria.CustomerAccountNumbers = customerAccountNumbers;
        }

        const contactPersonIds = this._activeRefinementCriteria
            .filter(refinement => refinement.sourceTypeValue === InvoiceRefinerSourceType.Contact && refinement.value)
            .flatMap(refinement => (refinement.value as string).split(','))
            .filter(id => id.length > 0);
        if (contactPersonIds.length > 0) {
            searchCriteria.ContactPersonIds = contactPersonIds;
        }

        const invoiceDateRange = this._getSelectedDateRange(InvoiceRefinerSourceType.InvoiceDate);
        if (invoiceDateRange) {
            searchCriteria.InvoiceDateFrom = invoiceDateRange.startDate;
            searchCriteria.InvoiceDateTo = invoiceDateRange.endDate;
        }

        const dueDateRange = this._getSelectedDateRange(InvoiceRefinerSourceType.DueDate);
        if (dueDateRange) {
            searchCriteria.DueDateFrom = dueDateRange.startDate;
            searchCriteria.DueDateTo = dueDateRange.endDate;
        }

        const invoiceIds = (this.state.invoiceNumberRefinerValue ?? '')
            .split(',')
            .map(invoiceId => invoiceId.trim())
            .filter(invoiceId => !StringExtensions.isNullOrWhitespace(invoiceId));
        if (invoiceIds.length > 0) {
            searchCriteria.InvoiceIds = invoiceIds;
        }
    }

    private _getSelectedDateRange(
        sourceType: InvoiceRefinerSourceType.InvoiceDate | InvoiceRefinerSourceType.DueDate
    ): IDateRange | undefined {
        const selectedDateRefiners = this._activeRefinementCriteria.filter(refinement => refinement.sourceTypeValue === sourceType);
        if (selectedDateRefiners.length !== 1) {
            return undefined;
        }

        const selectedDateRefiner = selectedDateRefiners[0];
        if (selectedDateRefiner.refinerItemId === DateRefinerOptionItem.Customized) {
            const startDate = getDateFromString(selectedDateRefiner.minValue);
            const endDate = getDateFromString(selectedDateRefiner.maxValue);

            // The end date is inclusive of the selected day, so extend it to the end of that day in local time.
            if (endDate) {
                endDate.setHours(23, 59, 59, 999);
            }

            return { startDate, endDate };
        }

        return this._getDateRange(selectedDateRefiner.refinerItemId as DateRefinerOptionItem);
    }

    private _getDateRange(dateRefinerOptionItem: DateRefinerOptionItem): IDateRange | undefined {
        const endDate = new Date();
        const startDate = new Date(endDate);

        switch (dateRefinerOptionItem) {
            case DateRefinerOptionItem.LastSevenDays:
                startDate.setDate(startDate.getDate() - 7);
                break;
            case DateRefinerOptionItem.LastThirtyDays:
                startDate.setDate(startDate.getDate() - 30);
                break;
            case DateRefinerOptionItem.LastThreeMonths:
                startDate.setMonth(startDate.getMonth() - 3);
                break;
            default:
                return undefined;
        }

        return { startDate, endDate };
    }

    private _resetInvoicePaging(): void {
        if (this._queryResultSettings.Paging) {
            this._queryResultSettings.Paging.Skip = 0;
        }
    }

    private _renderOrganizationWideSelector(): JSX.Element | null {
        const {
            context: {
                request: { user }
            },
            resources: {
                invoicesCurrentUserOptionLabel,
                invoicesCurrentUserOptionAriaLabel,
                invoicesOrganizationWideOptionLabel,
                invoicesOrganizationWideOptionAriaLabel,
                invoicesShowAllOptionLabel,
                invoicesShowAllOptionAriaLabel
            }
        } = this.props;

        if (!user?.isB2bContact) {
            return null;
        }

        const selectorClassName = `${this._className}__organization-wide-selector`;

        return (
            <InvoicesOrganizationWideSelectorComponent
                id={selectorClassName}
                typeName={this.props.typeName}
                className={selectorClassName}
                context={this.props.context}
                data={{}}
                resources={{
                    currentUserOptionLabel: invoicesCurrentUserOptionLabel,
                    currentUserOptionAriaLabel: invoicesCurrentUserOptionAriaLabel,
                    organizationWideOptionLabel: invoicesOrganizationWideOptionLabel,
                    organizationWideOptionAriaLabel: invoicesOrganizationWideOptionAriaLabel,
                    showAllOptionLabel: invoicesShowAllOptionLabel,
                    showAllOptionAriaLabel: invoicesShowAllOptionAriaLabel
                }}
                currentSelectorState={this.state.organizationWideSelectorState ?? InvoicesOrganizationWideSelectorState.CurrentUser}
                onChange={this._onOrganizationWideSelectorChange}
            />
        );
    }

    private _onOrganizationWideSelectorChange = (state: InvoicesOrganizationWideSelectorState): void => {
        if (!this.props.context.request.user?.isB2bContact) {
            return;
        }

        this.setState({ organizationWideSelectorState: state, invoiceNumberRefinerValue: '', isLoading: true }, () => {
            transaction(() => {
                this._activeRefinementCriteria = [];
            });
            void this._ensureContactRefinerSource(state);
            void this._refreshInvoices();
        });
    };

    private async _refreshInvoices(): Promise<void> {
        this.setState({ isLoading: true });
        this._resetInvoicePaging();
        const requestId = ++this._invoicesRequestId;
        const searchCriteria = await this._getInvoicesSearchCriteria(this.state.invoicesFilterState);
        const invoices = await getInvoices(
            new GetInvoicesInput(searchCriteria, this._queryResultSettings, false, this._isDescendingSort()),
            this.props.context.actionContext
        );
        if (requestId !== this._invoicesRequestId) {
            return;
        }
        this._initializeWithInvoices(invoices ?? []);
    }
}

export default InvoicesList;
